/**
 * Utility to apply CSS variables globally from a color palette
 * 
 * Supports three formats:
 * 1. Legacy format: { KEY: "value", GRADIENT_KEY: ["stop1", "stop2"] }
 * 2. 2nd Format (old): { colors: [{ label, color, isGradient, ... }] }
 * 3. New format: { colors: { "0": { label, color, isGradient, ... }, "1": { label, color: { label, color, ... }, ... } } }
 */
export const applyColorVariables = (colorPalette) => {
  if (!colorPalette) return;

  const root = document.documentElement;

  // Handle colors as array or object (convert object to array)
  const colorsData = colorPalette?.colors;
  if (colorsData && (Array.isArray(colorsData) || typeof colorsData === 'object')) {
    const colorsArray = Array.isArray(colorsData) ? colorsData : Object.values(colorsData);
    
    colorsArray.forEach((item) => {
      if (!item.label) return;
      
      // Extract actual color value (handles nested color objects)
      const colorValue = typeof item.color === 'object' && item.color?.color 
        ? item.color.color 
        : item.color;
      
      if (!colorValue) return;

      if (item.isGradient && Array.isArray(colorValue)) {
        // Gradient: color is an array of stops
        // Create indexed variables: --COLOR-PRIMARY_BUTTON_0, --COLOR-PRIMARY_BUTTON_1, etc.
        colorValue.forEach((stop, index) => {
          root.style.setProperty(`--COLOR-${item.label}_${index}`, stop);
        });
      } else {
        // Single color
        root.style.setProperty(`--COLOR-${item.label}`, colorValue);
      }
    });
    return;
  }

  // Legacy format: colorPalette is an object with key-value pairs
  Object.entries(colorPalette).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Convert array to a CSS-friendly string
      let index = 0;
      for (const item of value) {
        root.style.setProperty(`--COLOR-${key}_${index}`, item);
        index++;
      }
    } else {
      root.style.setProperty(`--COLOR-${key}`, value);
    }
  });
};


/**
 * Returns the color based on positive/negative value
 * @param {number} value - The value to check
 * @returns {string} - CSS color variable (green for positive, red for negative)
 */
export const getColorBasedOnValue = (value) => {
  return value >= 0 ? "var(--green-400)" : "var(--red-400)";
};
